'use client';

import React, { isValidElement } from 'react';
import { type ReactNode, type Ref, createContext, useContext } from 'react';
import { HelpCircleIcon } from '@milanote/roma-icons';
import type { InputProps as AriaInputProps, TextFieldProps as AriaTextFieldProps } from 'react-aria-components';
import { Group as AriaGroup, Input as AriaInput, TextField as AriaTextField } from 'react-aria-components';
import { Field } from '../field';
import { Tooltip } from '../tooltip';
import { cx, sortCx } from '../../../utils/cx';
import { isReactComponent } from '../../../utils/isReactComponent';

const SEMANTIC_NAME_BASE = 'RomaInputBase';
const SEMANTIC_NAME_TEXT_FIELD = 'RomaTextField';
const SEMANTIC_NAME = 'RomaInput';

const TextFieldContext = createContext<TextFieldProps>({});

export interface InputBaseProps extends TextFieldProps {
    /**
     * Input size.
     * @default "sm"
     */
    size?: 'sm' | 'md' | 'lg';
    /** Placeholder text. */
    placeholder?: string;
    /** Class name for the input. */
    inputClassName?: string;
    /** Class name for the input wrapper. */
    wrapperClassName?: string;
    /** Class name for the tooltip. */
    tooltipClassName?: string;
    groupRef?: Ref<HTMLDivElement>;
    /** Icon component or element to display on the left side of the input. */
    iconLeading?: React.FC<{ className?: string }> | ReactNode;
    /**
     * Icon component or element to display on the right side of the input.
     * Note: Takes precedence over `tooltip` when both are provided.
     */
    iconTrailing?: React.FC<{ className?: string }> | ReactNode;
    /**
     * Tooltip message on hover.
     * Note: Will not be displayed if `iconTrailing` is provided, as `iconTrailing` takes precedence.
     */
    tooltip?: string;
}

const styles = sortCx({
    sizes: {
        sm: {
            wrapper: 'rounded-xs',
            input: 'rounded-xs px-3 py-1.5 text-sm',
            input_withTrailingIcon: 'pr-9',
            input_withLeadingIcon: 'pl-10',
            iconLeading: 'left-3',
            iconTrailing: 'right-3',
        },
        md: {
            wrapper: 'rounded-xs',
            input: 'rounded-xs px-3.5 py-2.5 text-md',
            input_withTrailingIcon: 'pr-9.5',
            input_withLeadingIcon: 'pl-10.5',
            iconLeading: 'left-3.5',
            iconTrailing: 'right-3.5',
        },
        lg: {
            wrapper: 'rounded-sm',
            input: 'rounded-sm px-3.5 py-2.5 text-lg',
            input_withTrailingIcon: 'pr-9.5',
            input_withLeadingIcon: 'pl-10.5',
            iconLeading: 'left-3.5',
            iconTrailing: 'right-3.5',
        },
    },
});

export const InputBase = React.forwardRef<HTMLInputElement, Omit<InputBaseProps, 'label' | 'hint'>>(
    (
        {
            tooltip,
            groupRef,
            size = 'sm',
            isInvalid,
            isDisabled,
            iconLeading: LeadingIcon,
            iconTrailing: TrailingIcon,
            placeholder,
            wrapperClassName,
            tooltipClassName,
            inputClassName,
            // Omit this prop to avoid invalid HTML attribute warning
            isRequired: _isRequired,
            ...inputProps
        },
        ref,
    ) => {
        // Check if the input has trailing or leading icons
        const hasTrailingIcon = !!TrailingIcon || !!tooltip;
        const hasLeadingIcon = !!LeadingIcon;

        // If the input is inside a `TextFieldContext`, use its context to simplify applying styles
        const context = useContext(TextFieldContext);

        const inputSize = context?.size || size;
        const inputIsInvalid = isInvalid ?? context?.isInvalid;
        const inputIsDisabled = isDisabled ?? context?.isDisabled;

        // Ring styles
        const inputRingStyles = cx(
            // Base ring styles
            'ring-1 ring-inset ring-primary transition duration-100 ease-linear',

            // Disabled state styles
            inputIsDisabled && 'ring-disabled',
            'group-disabled:ring-disabled',

            // Invalid state styles
            inputIsInvalid && 'ring-error_subtle',

            // Focus styles
            'focus:ring-1 data-[focus-visible]:ring-2',
            !inputIsInvalid && 'focus:ring-quarternary data-[focus-visible]:ring-primary_focused',

            // Invalid state with focus styles
            inputIsInvalid && 'focus:ring-error',
        );

        return (
            <AriaGroup
                isDisabled={inputIsDisabled}
                isInvalid={inputIsInvalid}
                ref={groupRef}
                className={cx(
                    SEMANTIC_NAME_BASE,
                    'relative flex w-full flex-row place-content-center place-items-center bg-primary duration-100 ease-linear ring-inset',
                    styles.sizes[inputSize].wrapper,

                    // Disabled state styles
                    inputIsDisabled && 'cursor-default bg-disabled_subtle ring-disabled',
                    'group-disabled:cursor-default group-disabled:bg-disabled_subtle group-disabled:ring-disabled',

                    context?.wrapperClassName,
                    wrapperClassName,
                )}
            >
                {/* Leading icon */}
                {isValidElement(LeadingIcon) && LeadingIcon}
                {isReactComponent(LeadingIcon) && (
                    <LeadingIcon
                        className={cx(
                            'absolute size-5 text-fg-quaternary',
                            inputIsDisabled && 'text-fg-disabled',
                            styles.sizes[inputSize].iconLeading,
                        )}
                    />
                )}

                {/* Input field */}
                <AriaInput
                    {...(inputProps as AriaInputProps)}
                    ref={ref}
                    placeholder={placeholder}
                    className={cx(
                        'm-0 w-full bg-transparent text-primary outline-hidden placeholder:text-placeholder',
                        // autofill in chrome / firefox
                        'autofill:text-primary autofill:bg-transparent',
                        // disabled state
                        inputIsDisabled && 'cursor-default text-disabled overflow-ellipsis',
                        'group-disabled:cursor-default group-disabled:text-disabled group-disabled:overflow-ellipsis',
                        // ring styles
                        inputRingStyles,
                        // input size styles
                        styles.sizes[inputSize].input,
                        // padding adjustments when leading/trailing icons are present
                        hasLeadingIcon && styles.sizes[inputSize].input_withLeadingIcon,
                        hasTrailingIcon && styles.sizes[inputSize].input_withTrailingIcon,
                        // context and prop class names
                        context?.inputClassName,
                        inputClassName,
                    )}
                />

                {/* Trailing icon - takes precedence over tooltip */}
                {isValidElement(TrailingIcon) && TrailingIcon}
                {isReactComponent(TrailingIcon) && (
                    <TrailingIcon
                        className={cx(
                            'absolute size-5 text-fg-quaternary',
                            inputIsDisabled && 'text-fg-disabled',
                            styles.sizes[inputSize].iconTrailing,
                        )}
                    />
                )}

                {/* Tooltip and help icon - only shown if no trailing icon is provided */}
                {tooltip && !TrailingIcon && (
                    <Tooltip title={tooltip} placement="top" arrow>
                        <Tooltip.Trigger
                            className={cx(
                                'absolute cursor-pointer text-fg-quaternary transition duration-200 hover:text-fg-quaternary_hover focus:text-fg-quaternary_hover',
                                styles.sizes[inputSize].iconTrailing,
                                context?.tooltipClassName,
                                tooltipClassName,
                            )}
                        >
                            <HelpCircleIcon className="size-4" />
                        </Tooltip.Trigger>
                    </Tooltip>
                )}
            </AriaGroup>
        );
    },
);

InputBase.displayName = SEMANTIC_NAME_BASE;

interface BaseProps {
    /** Label text for the input */
    label?: string;
    /** Helper text displayed below the input */
    hint?: ReactNode;
}

export interface TextFieldProps
    extends BaseProps,
        AriaTextFieldProps,
        Pick<InputBaseProps, 'size' | 'wrapperClassName' | 'inputClassName' | 'tooltipClassName'> {
    ref?: Ref<HTMLDivElement>;
}

export const TextField = ({ className, ...props }: TextFieldProps) => {
    return (
        <TextFieldContext.Provider value={props}>
            <AriaTextField
                {...props}
                data-input-wrapper
                className={(state) =>
                    cx(
                        SEMANTIC_NAME_TEXT_FIELD,
                        'group flex h-max w-full flex-col items-start justify-start gap-1.5',
                        typeof className === 'function' ? className(state) : className,
                    )
                }
            />
        </TextFieldContext.Provider>
    );
};

TextField.displayName = SEMANTIC_NAME_TEXT_FIELD;

export interface InputProps extends InputBaseProps, BaseProps {
    /** Whether to hide required indicator from label */
    hideRequiredIndicator?: boolean;
}

const InputRoot = React.forwardRef<HTMLInputElement, InputProps>(
    (
        {
            size = 'sm',
            placeholder,
            iconLeading,
            iconTrailing,
            label,
            hint,
            hideRequiredIndicator,
            className,
            groupRef,
            tooltip,
            inputClassName,
            wrapperClassName,
            tooltipClassName,
            ...props
        },
        ref,
    ) => {
        return (
            <Field as={TextField} aria-label={!label ? placeholder : undefined} {...props} className={className}>
                {({ isRequired, isInvalid }) => (
                    <>
                        {label && (
                            <Field.Label
                                size={size}
                                isRequired={hideRequiredIndicator ? !hideRequiredIndicator : isRequired}
                            >
                                {label}
                            </Field.Label>
                        )}

                        <InputBase
                            {...{
                                ref,
                                groupRef,
                                size,
                                placeholder,
                                iconLeading,
                                iconTrailing,
                                inputClassName,
                                wrapperClassName,
                                tooltipClassName,
                                tooltip,
                                isInvalid,
                            }}
                        />

                        {hint && <Field.HintText isInvalid={isInvalid}>{hint}</Field.HintText>}
                    </>
                )}
            </Field>
        );
    },
);

InputRoot.displayName = SEMANTIC_NAME;

export const Input = Object.assign(InputRoot, {
    Base: InputBase,
});
